// Service API général - gère les tokens JWT et les requêtes HTTP
// Hôte de l'API : api.mbiyo.org (Cloudflare — pare-feu, Turnstile, contournement du blocage FAI).
// Sécurité 2026-11-02 : une valeur d'environnement qui pointerait encore vers l'hôte Railway direct est ignorée.
const RAW_API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE_URL = RAW_API_URL && !/up\.railway\.app/i.test(RAW_API_URL)
  ? RAW_API_URL
  : (
  window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : 'https://api.mbiyo.org/api'
);

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Récupérer le token depuis localStorage
  getToken(userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    return localStorage.getItem(key);
  }

  // Stocker le token
  setToken(token, userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    if (token) {
      localStorage.setItem(key, token);
    }
  }

  // Supprimer le token
  removeToken(userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    localStorage.removeItem(key);
  }

  // Requête HTTP générique
  async request(endpoint, options = {}) {
    const userType = options.userType || 'user';
    const token = this.getToken(userType);
    const timeout = options.timeout || 30000; // 30 secondes par défaut pour éviter les timeouts

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      console.log(`📡 API Request: ${options.method || 'GET'} ${this.baseURL}${endpoint}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        data = { error: 'Erreur lors du parsing de la réponse' };
      }

      if (!response.ok) {
        console.error(`❌ API Error (${response.status}):`, data);
        // Auto-logout si token invalide (401) SAUF sur les routes d'auth
        // Anti-boucle : au plus UN reload de déconnexion par chargement de page,
        // et jamais sans jeton stocké (un poller en 401 ne doit pas boucler).
        if (response.status === 401 && !endpoint.startsWith('/auth/') && !options.silent401) {
          const hadToken = localStorage.getItem('adminToken');
          if (hadToken && !ApiService._didAuthReload) {
            ApiService._didAuthReload = true;
            localStorage.removeItem('adminToken');
            localStorage.removeItem('currentAdmin');
            window.location.reload();
            return;
          }
        }
        const errorMessage = data.error || 'Erreur serveur';
        const error = new Error(errorMessage);
        error.status = response.status;
        error.error = errorMessage;
        throw error;
      }

      console.log(`✅ API Success: ${options.method || 'GET'} ${endpoint}`);
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ API Timeout:', endpoint);
        throw new Error('Timeout - La requête a dépassé ' + Math.floor(timeout/1000) + ' secondes');
      }
      console.error('❌ API Error:', endpoint, error);
      throw error;
    }
  }

  // GET
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST
  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // PUT
  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  // PATCH
  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  // DELETE
  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // UPLOAD (multipart/form-data)
  async upload(endpoint, formData, options = {}) {
    const userType = options.userType || 'admin';
    const token = this.getToken(userType);
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    return data;
  }
}

const apiService = new ApiService();
export default apiService;
